import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useWhatsAppMediaUrl } from '@/hooks/useWhatsAppMediaUrl';
import { retryWhatsAppMedia } from '@/services/whatsapp.service';
import { sanitizeSupabaseError } from '@/lib/errorHandler';

export interface ChatMediaContentProps {
  mensagemId: string;
  tipoMedia: 'image' | 'audio' | 'video' | 'document';
  mediaStatus: 'pending' | 'ready' | 'failed';
  mediaStoragePath: string | null;
  mediaMimeType: string | null;
  mediaFilename: string | null;
  /** Canal da mensagem — 'app' assina contra app-chat-media; outros contra whatsapp-media. */
  canal?: string | null;
}

export function ChatMediaContent({
  mensagemId,
  tipoMedia,
  mediaStatus,
  mediaStoragePath,
  mediaMimeType: _mediaMimeType,
  mediaFilename,
  canal,
}: ChatMediaContentProps) {
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);
  const { data: url, isLoading } = useWhatsAppMediaUrl(
    mediaStoragePath,
    mediaStatus === 'ready',
    canal,
  );

  if (mediaStatus === 'pending') {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>A carregar…</span>
      </div>
    );
  }

  if (mediaStatus === 'failed') {
    return (
      <div className="flex flex-col gap-2 py-2">
        <div className="flex items-center gap-2 text-xs text-destructive">
          <AlertCircle className="h-4 w-4" />
          <span>Media indisponível</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          disabled={retrying}
          onClick={async () => {
            setRetrying(true);
            try {
              const { mediaStatus } = await retryWhatsAppMedia(mensagemId);
              if (mediaStatus === 'ready') {
                toast.success('Media carregada');
              } else {
                toast.error('Não foi possível carregar a media');
              }
              queryClient.invalidateQueries({ queryKey: ['mensagens'] });
              queryClient.invalidateQueries({ queryKey: ['cliente-media'] });
            } catch (error) {
              toast.error(sanitizeSupabaseError(error));
            } finally {
              setRetrying(false);
            }
          }}
        >
          {retrying ? 'A tentar…' : 'Tentar novamente'}
        </Button>
      </div>
    );
  }

  // mediaStatus === 'ready'
  if (isLoading || !url) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" aria-label="A carregar" />
      </div>
    );
  }

  if (tipoMedia === 'image') {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img
          src={url}
          alt="Imagem recebida"
          className="rounded-lg max-w-full max-h-64 object-cover"
        />
      </a>
    );
  }

  if (tipoMedia === 'audio') {
    return <audio controls src={url} className="max-w-full" />;
  }

  if (tipoMedia === 'video') {
    return <video controls src={url} className="rounded-lg max-w-full max-h-64" />;
  }

  // tipoMedia === 'document'
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2 rounded-lg border border-border/50 px-3 py-2 hover:bg-muted/50 transition-colors"
      download={mediaFilename ?? undefined}
    >
      <FileText className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
      <span className="text-sm truncate flex-1">{mediaFilename ?? 'Documento'}</span>
      <Download className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
    </a>
  );
}
